import {
  ChangeDetectionStrategy,
  Component,
  input,
  linkedSignal,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

import {
  ChipSelectorComponent,
  EditModeDirective,
  InlineEditorComponent,
  ProfileCardComponent,
  ProfileCardSize,
  ProfileCardSizeEnum,
  ViewModeDirective,
} from '@erm/cluster';

import { WcgMenuOption } from '@wcg/frontend-framework';
import {
  CreateContactDegreeRequest,
  CreateContactSpecialtyRequest,
  DesignationType,
} from '@data-access/profiles';

@Component({
  selector: 'wcg-profile-designation-wrapper',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    ProfileCardComponent,
    MatButtonModule,
    InlineEditorComponent,
    EditModeDirective,
    ViewModeDirective,
    MatIconModule,
    MatChipsModule,
    ChipSelectorComponent,
    DragDropModule,
  ],
  styleUrls: ['./designation-wrapper.component.scss'],
  templateUrl: './designation-wrapper.component.html',
})
export class DesignationWrapperComponent {
  readonly label = input<string>('Appears as: Samantha Jones MD, MBBS, PhD');
  readonly designationType = input.required<DesignationType>();
  readonly title = input<string>('');
  readonly designations = input<WcgMenuOption[]>([]);
  readonly options = input<WcgMenuOption[]>([]);
  readonly canEdit = input<boolean>(false);
  readonly cardSize = input<ProfileCardSize>(ProfileCardSizeEnum.Medium);
  readonly icon = input<string>('');
  readonly loading = input<boolean>(false);
  readonly error = input<Error | null>(null);

  readonly updateDesignations = output<
    CreateContactSpecialtyRequest[] | CreateContactDegreeRequest[]
  >();

  readonly editMode = signal<boolean>(false);

  readonly $designations = linkedSignal({
    source: this.designations,
    computation: (designations) => {
      if (!designations) {
        return [];
      }
      return [...designations];
    },
  });

  enterEditMode(): void {
    if (this.canEdit()) {
      this.editMode.set(true);
    }
  }

  removeDesignation(designation: WcgMenuOption, $event: Event) {
    if ($event) {
      $event.stopPropagation();
    }
    this.$designations.update((designations) =>
      designations.filter((d) => d.id !== designation.id),
    );
    this._handleUpdateDesignations(this.$designations());
  }

  handleSelectedChips(chips: WcgMenuOption[]) {
    this.editMode.set(false);
    this._handleUpdateDesignations(chips);
  }

  handleChipDrop(event: CdkDragDrop<WcgMenuOption[]>) {
    this.$designations.update((designations) => {
      moveItemInArray(designations, event.previousIndex, event.currentIndex);
      return [...designations];
    });
    this.updateDesignations.emit(
      this.$designations().map((d, index) => ({
        name: d.id!,
        sort_order: index,
      })),
    );
  }

  private _handleUpdateDesignations(chips: WcgMenuOption[]) {
    const _selectedDesignations:
      | CreateContactDegreeRequest[]
      | CreateContactSpecialtyRequest[] = chips.map((chip, index) => ({
      name: chip.id!,
      sort_order: index,
    }));
    this.updateDesignations.emit(_selectedDesignations);
  }
}
